/**
 * The closed vocabulary the usage tracker may send (#1110, D218). A mirror of
 * the lists in the API's usage module (#1107), kept as plain data so the
 * tracker has no way to say a word the server would refuse.
 *
 * Every list below must equal the server's. `api/test/usage.test.ts` parses
 * this file and that one and fails when they differ, so a word added on one
 * side only is a red build, not a silent drift.
 *
 * The D216 player ribbon is not built; `ribbon` joins USAGE_NAV_VIA when it is.
 */

export const USAGE_SCREENS = [
  "/dashboard",
  "/onboarding",
  "/development",
  "/performance",
  "/statistics",
  "/grounds-map",
  "/learn",
  "/learn/review",
  "/learn/:category/:slug",
  "/coach-setup",
  "/coach-portal",
  "/coach-portal/record",
  "/insights",
  "/tour",
  "/tour/coach",
  "/coach-portal/team",
  "/coach-portal/games",
  "/coach-portal/games/schedule",
  "/plan",
  "/plan/return",
  "/plan/failed",
  "/settings",
  "/settings/delete-account",
  "/privacy",
  "/terms",
];

export const USAGE_TABS = {
  // #1164: Plan's three sections are the new words; the five old ones still resolve.
  "/development": ["identity", "reflections", "short", "long", "feedback", "coming-up", "goals", "more"],
  // #1128: the page is two sections (`?section=training|guides`) with the eight
  // topics inside Guides (`?topic=`); the old ten `?tab=` values still resolve
  // and are still sent. `guides` is the one new word: a topic view reports its
  // topic, My training reports `training`, the Guides list reports `guides`.
  "/learn": [
    "training",
    "drills",
    "guides",
    "assigned",
    "batting",
    "bowling",
    "fielding",
    "wicketkeeping",
    "field-placement",
    "fitness",
    "nutrition",
    "mindset",
  ],
  "/coach-portal": ["identity", "goals", "feedback", "guides", "match-reflections", "reflections"],
  "/onboarding": ["profile", "identity"],
  "/coach-portal/team": ["pick", "notes"],
};

export const USAGE_NAV_VIA = [
  "destination_row",
  "coach_shortcut",
  "account_menu",
  "bell",
  "back",
  "in_page",
  "deep_link",
  "unknown",
];

export const CLIENT_USAGE_EVENTS = {
  screen_view: [],
  "log.start": [],
  "log.step_done": ["ground", "scorecard"],
  "log.saved": [],
  "log.left_unsaved": ["ground", "scorecard"],
  "log.near_me": [],
  "log.search": [],
  "log.add_ground": [],
  "record.fold_open": [],
  "plan.goal_create": [],
  "grounds.card_open": [],
  "grounds.near_me": [],
  "grounds.search": [],
  "grounds.add": [],
  "training.new_session": [],
  "settings.fold_open": [
    "health",
    "notifications",
    "feedback",
    "usage",
    "billing",
    "player_profile",
    "coach_access",
    "add_player",
    "passkeys",
    "text_size",
    "delete_account",
  ],
  "menu.open": ["account", "bell", "accessibility"],
  "menu.choose": ["identity", "role_switch", "training", "settings", "sign_out"],
  "coach.shortcut": ["goal", "feedback", "guide", "training"],
  "coach.player_open": [],
  "signup.done": [],
  "onboarding.step_done": ["profile", "identity"],
  "onboarding.done": [],
  "tour.done": [],
  "tour.skipped": [],
  "grounds.chip": ["played", "near_me", "all"],
  "coach.invite_sent": [],
  "coach.invite_accepted": [],
  "xi.picked": ["complete", "partial"],
  "plan.reached": [],
  "plan.checkout_started": [],
  "plan.return_paid": [],
  "plan.return_failed": [],
  "billing.stop": [],
  "billing.keep_plan": [],
};

const CLIENT_EVENT_FIELDS = ["name", "screen", "tab", "item", "via"];

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * Is this event drawn entirely from the closed lists? A copy of the server's
 * `isAllowedClientEvent`: pure, total, never throws. A field is present
 * unless it is `undefined`; every present field must be a string found by
 * exact comparison in a list above, and any key outside name, screen, tab,
 * item and via refuses the event.
 */
export function isAllowedClientEvent(e) {
  if (typeof e !== "object" || e === null || Array.isArray(e)) return false;
  for (const key of Reflect.ownKeys(e)) {
    if (typeof key !== "string" || !CLIENT_EVENT_FIELDS.includes(key)) return false;
  }
  const { name, screen, tab, item, via } = e;
  if (typeof name !== "string" || !hasOwn(CLIENT_USAGE_EVENTS, name)) return false;
  const allowedItems = CLIENT_USAGE_EVENTS[name];

  if (screen === undefined) {
    if (name === "screen_view") return false;
  } else if (typeof screen !== "string" || !USAGE_SCREENS.includes(screen)) {
    return false;
  }

  if (tab !== undefined) {
    if (typeof tab !== "string" || typeof screen !== "string" || !hasOwn(USAGE_TABS, screen)) return false;
    if (!USAGE_TABS[screen].includes(tab)) return false;
  }

  if (item !== undefined && (typeof item !== "string" || !allowedItems.includes(item))) return false;
  if (via !== undefined && (typeof via !== "string" || !USAGE_NAV_VIA.includes(via))) return false;
  return true;
}
